import type { ChangeEvent } from "react";
import { action, computed, makeObservable, observable } from "mobx";
import { inject, injectable } from "tsyringe";
import type { InputOnChangeData } from "../Input/Input";
import { ViewModel } from "../../lib/vvm";
import { STORE_TOKEN } from "../../stores/store";
import type {
  IStore,
  SearchMeasureFilter,
  SearchPlacementFilter,
  SearchTypeFilterOption,
} from "../../stores/store";
import { UI_STORE_TOKEN } from "../../stores/UIStore";
import type { IUIStore } from "../../stores/UIStore";
import {
  buildNextSavedPresets,
  isPresetApplied,
  loadSavedFilterPresets,
  MAX_SEARCH_PRESETS,
  persistSavedFilterPresets,
  QUICK_SEARCH_PRESETS,
  SEARCH_MEASURE_OPTIONS,
  SEARCH_PLACEMENT_OPTIONS,
  SEARCH_PRESETS_STORAGE_KEY,
  type SearchFilterPreset,
  type SearchFiltersSnapshot,
} from "./viewmodel/presets";

const SEARCH_DEBOUNCE_MS = 260;

@injectable()
export class SearchViewModel extends ViewModel {
  @observable
  private searchInputValue = "";

  @observable.shallow
  private filterPresets: SearchFilterPreset[] = [];

  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    @inject(STORE_TOKEN) private readonly store: IStore,
    @inject(UI_STORE_TOKEN) private readonly uiStore: IUIStore,
  ) {
    super();

    makeObservable(this);
    this.syncInputValueWithStore();
    this.loadFilterPresets();
  }

  protected onViewMounted(): void {
    this.syncInputValueWithStore();
    this.loadFilterPresets();
  }

  protected onViewUnmounted(): void {
    this.clearSearchDebounceTimer();
  }

  @computed
  get searchText(): string {
    return this.searchInputValue;
  }

  @computed
  get measureFilter(): SearchMeasureFilter {
    return this.store.searchMeasureFilter;
  }

  @computed
  get placementFilter(): SearchPlacementFilter {
    return this.store.searchPlacementFilter;
  }

  @computed
  get typeFilter(): string {
    return this.store.searchTypeFilter;
  }

  @computed
  get typeOptions(): SearchTypeFilterOption[] {
    return this.store.searchTypeOptions;
  }

  @computed
  get hasActiveFilters(): boolean {
    return this.store.hasActiveSearchFilters;
  }

  @computed
  get hasSearchQuery(): boolean {
    return this.searchInputValue.trim().length > 0;
  }

  @computed
  get canReset(): boolean {
    return this.hasActiveFilters || this.hasSearchQuery;
  }

  @computed
  get quickPresets(): SearchFilterPreset[] {
    return QUICK_SEARCH_PRESETS;
  }

  @computed
  get savedFilterPresets(): SearchFilterPreset[] {
    return this.filterPresets;
  }

  @computed
  get canSavePreset(): boolean {
    return this.hasActiveFilters;
  }

  @computed
  get measureOptions(): Array<{ value: SearchMeasureFilter; label: string }> {
    return SEARCH_MEASURE_OPTIONS;
  }

  @computed
  get placementOptions(): Array<{
    value: SearchPlacementFilter;
    label: string;
  }> {
    return SEARCH_PLACEMENT_OPTIONS;
  }

  @action.bound
  onChange = (
    event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void => {
    const nextValue = data.value || event.target.value;
    this.searchInputValue = nextValue;
    this.uiStore.onSearchFocus();
    this.scheduleSearch(nextValue);
  };

  @action.bound
  onFocus = (): void => {
    this.uiStore.onSearchFocus();
  };

  @action.bound
  onBlur = (): void => {
    const hasSearchQuery = this.searchInputValue.trim().length > 0;
    if (hasSearchQuery || this.store.hasActiveSearchFilters) {
      return;
    }

    this.uiStore.onSearchBlur();
  };

  @action.bound
  onMeasureFilterChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    this.applySearchFilterChange(() => {
      this.store.setSearchMeasureFilter(
        event.target.value as SearchMeasureFilter,
      );
    });
  };

  @action.bound
  onPlacementFilterChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    this.applySearchFilterChange(() => {
      this.store.setSearchPlacementFilter(
        event.target.value as SearchPlacementFilter,
      );
    });
  };

  @action.bound
  onTypeFilterChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    this.applySearchFilterChange(() => {
      this.store.setSearchTypeFilter(event.target.value);
    });
  };

  @action.bound
  clearFilters = (): void => {
    if (!this.canReset) {
      return;
    }

    const hadSearchQuery = this.hasSearchQuery;
    this.clearSearchDebounceTimer();
    this.searchInputValue = "";
    this.store.clearSearchFilters();
    if (hadSearchQuery) {
      this.store.search("");
    }

    const hasQueryAfterReset = this.store.searchText.trim().length > 0;
    const hasFiltersAfterReset = this.store.hasActiveSearchFilters;
    if (!hasQueryAfterReset && !hasFiltersAfterReset) {
      this.uiStore.onSearchBlur();
      return;
    }

    this.uiStore.onSearchFocus();
  };

  @action.bound
  isQuickPresetActive(presetId: string): boolean {
    const preset = QUICK_SEARCH_PRESETS.find((item) => item.id === presetId);
    if (!preset) {
      return false;
    }

    return isPresetApplied(preset, this.currentFiltersSnapshot);
  }

  @action.bound
  isSavedPresetActive(presetId: string): boolean {
    const preset = this.filterPresets.find((item) => item.id === presetId);
    if (!preset) {
      return false;
    }

    return isPresetApplied(preset, this.currentFiltersSnapshot);
  }

  @action.bound
  applyQuickPreset(presetId: string): void {
    this.applyPresetById(presetId, QUICK_SEARCH_PRESETS);
  }

  @action.bound
  applySavedPreset(presetId: string): void {
    this.applyPresetById(presetId, this.filterPresets);
  }

  @action.bound
  removeSavedPreset(presetId: string): void {
    this.filterPresets = this.filterPresets.filter(
      (item) => item.id !== presetId,
    );
    this.persistFilterPresets();
  }

  @action.bound
  saveCurrentPreset(): void {
    if (!this.canSavePreset) {
      return;
    }

    const nextPresets = buildNextSavedPresets({
      existingPresets: this.filterPresets,
      filters: this.currentFiltersSnapshot,
      measureOptions: this.measureOptions,
      placementOptions: this.placementOptions,
      typeOptions: this.typeOptions,
      maxPresets: MAX_SEARCH_PRESETS,
      promptLabel: (defaultLabel) =>
        typeof window !== "undefined"
          ? window.prompt("Название пресета", defaultLabel)
          : defaultLabel,
    });

    if (!nextPresets) {
      return;
    }

    this.filterPresets = nextPresets;
    this.persistFilterPresets();
  }

  private get currentFiltersSnapshot(): SearchFiltersSnapshot {
    return {
      measureFilter: this.measureFilter,
      placementFilter: this.placementFilter,
      typeFilter: this.typeFilter,
    };
  }

  @action.bound
  private applySearchFilterChange(applyFilter: () => void): void {
    this.flushSearchNow();
    applyFilter();
    this.uiStore.onSearchFocus();
  }

  @action.bound
  private applyPresetById(presetId: string, presets: SearchFilterPreset[]): void {
    const preset = presets.find((item) => item.id === presetId);
    if (!preset) {
      return;
    }

    this.applyPreset(preset);
  }

  @action.bound
  private scheduleSearch(value: string): void {
    this.clearSearchDebounceTimer();
    this.searchDebounceTimer = setTimeout(() => {
      this.searchDebounceTimer = null;
      this.store.search(value);
    }, SEARCH_DEBOUNCE_MS);
  }

  @action.bound
  private flushSearchNow(): void {
    if (!this.searchDebounceTimer) {
      return;
    }

    this.clearSearchDebounceTimer();
    this.store.search(this.searchInputValue);
  }

  @action.bound
  private clearSearchDebounceTimer(): void {
    if (!this.searchDebounceTimer) {
      return;
    }

    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = null;
  }

  @action.bound
  private syncInputValueWithStore(): void {
    this.searchInputValue = this.store.searchText || "";
  }

  @action.bound
  private applyPreset(preset: SearchFilterPreset): void {
    this.flushSearchNow();
    this.store.setSearchMeasureFilter(preset.measureFilter);
    this.store.setSearchPlacementFilter(preset.placementFilter);
    this.store.setSearchTypeFilter(preset.typeFilter);
    this.uiStore.onSearchFocus();
  }

  @action.bound
  private loadFilterPresets(): void {
    const storage =
      typeof localStorage === "undefined" ? undefined : localStorage;
    this.filterPresets = loadSavedFilterPresets({
      storage,
      storageKey: SEARCH_PRESETS_STORAGE_KEY,
      maxPresets: MAX_SEARCH_PRESETS,
    });
  }

  private persistFilterPresets(): void {
    const storage =
      typeof localStorage === "undefined" ? undefined : localStorage;
    persistSavedFilterPresets({
      storage,
      presets: this.filterPresets,
      storageKey: SEARCH_PRESETS_STORAGE_KEY,
    });
  }
}
